import { getCSRFToken } from "./django";

export const Client = Object.freeze({
  BROWSER: "browser",
});

// Environment-aware settings
const isDevelopment = process.env.NODE_ENV === "development";


export const settings = {
  client: Client.BROWSER,
  baseUrl: isDevelopment
    ? `http://localhost:8002`
    : `${window.location.origin}/api`,
  withCredentials: true,
};


const ACCEPT_JSON = {
  accept: "application/json",
};

export const URLs = Object.freeze({
  CONFIG: "/config",
  LOGIN: "/auth/login",
  SESSION: "/auth/session",
  SIGNUP: "/auth/signup",
  EMAIL: "/account/email",
  VERIFY_EMAIL: "/auth/email/verify",
  REAUTHENTICATE: "/auth/reauthenticate",
  REQUEST_PASSWORD_RESET: "/auth/password/request",
  RESET_PASSWORD: "/auth/password/reset",
  USER_GROUPS: "/acc/user_groups",
  MFA_AUTHENTICATE: "/auth/2fa/authenticate",
  MFA_TOTP_AUTHENTICATOR: "/account/authenticators/totp",
});

const tokenStorage = window.sessionStorage;

export function getSessionToken() {
  return tokenStorage.getItem("sessionToken");
}

async function request(method, path, data, headers, authfunc = true) {
  const options = {
    method,
    headers: {
      ...ACCEPT_JSON,
      ...headers,
    },
  };
  if (settings.withCredentials) {
    options.credentials = "include";
  }

  if (path !== URLs.CONFIG && settings.client === Client.BROWSER) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }
  }

  if (typeof data !== "undefined") {
    options.body = JSON.stringify(data);
    options.headers["Content-Type"] = "application/json";
  }

  let baseUrl = settings.baseUrl;
  if (authfunc) {
    baseUrl = new URL(settings.baseUrl + `/_allauth/${Client.BROWSER}/v1`);
  }

  const resp = await fetch(baseUrl + path, options);
  const msg = await resp.json();

  if (msg.status === 410) {
    tokenStorage.removeItem("sessionToken");
  }
  if (msg.meta?.session_token) {
    tokenStorage.setItem("sessionToken", msg.meta.session_token);
  }

  if (
    [401, 410].includes(msg.status) ||
    (msg.status === 200 && msg.meta?.is_authenticated)
  ) {
    const event = new CustomEvent("allauth.auth.change", { detail: msg });
    document.dispatchEvent(event);
  }

  return msg;
}

export async function login(data) {
  return await request("POST", URLs.LOGIN, data);
}

export async function logout() {
  return await request("DELETE", URLs.SESSION);
}

export async function signUp(data) {
  return await request("POST", URLs.SIGNUP, data);
}

export async function getConfig() {
  return await request("GET", URLs.CONFIG);
}

export async function getAuth() {
  return await request("GET", URLs.SESSION);
}

export async function addEmail(email) {
  return await request("POST", URLs.EMAIL, { email });
}

export async function verifyEmail(key) {
  return await request("POST", URLs.VERIFY_EMAIL, { key });
}

export async function getEmailAddresses() {
  return await request("GET", URLs.EMAIL);
}

export async function getCurrentSession() {
  try {
    const response = await request("GET", URLs.SESSION);
    return response;
  } catch (error) {
    return null;
  }
}

export async function requestPasswordReset(email) {
  return await request("POST", URLs.REQUEST_PASSWORD_RESET, { email });
}

export async function resetPassword(data) {
  return await request("POST", URLs.RESET_PASSWORD, data);
}

export async function markEmailAsPrimary(email) {
  return await request("PATCH", URLs.EMAIL, { email, primary: true });
}

export async function requestEmailVerification(email) {
  return await request("POST", URLs.VERIFY_EMAIL + "/resend", { email });
}

export async function getUserGroups() {
  return await request("GET", URLs.USER_GROUPS, undefined, undefined, false);
}

// ─── ADMIN API ────────────────────────────────────────────────────────────────

export async function adminGetUsers() {
  return await request("GET", "/acc/admin/users/", undefined, undefined, false);
}

export async function adminCreateUser(data) {
  return await request("POST", "/acc/admin/users/create/", data, undefined, false);
}

export async function adminUpdateUser(id, data) {
  return await request("PATCH", `/acc/admin/users/${id}/`, data, undefined, false);
}

export async function adminDeleteUser(id) {
  return await request("DELETE", `/acc/admin/users/${id}/delete/`, undefined, undefined, false);
}

export async function adminResendInvite(id) {
  return await request("POST", `/acc/admin/users/${id}/resend-invite/`, undefined, undefined, false);
}

export async function getNotifications() {
  return await request("GET", "/acc/notifications/", undefined, undefined, false);
}

export async function markNotificationsRead() {
  return await request("POST", "/acc/notifications/read/", undefined, undefined, false);
}

export async function adminGetRetentionConfigs() {
  // Using authfunc=false to bypass the _allauth base URL
  return await request("GET", "/acc/admin/retention/", undefined, undefined, false);
}

export async function adminUpdateRetentionConfig(id, data) {
  return await request("PATCH", `/acc/admin/retention/${encodeURIComponent(id)}/`, data, undefined, false);
}
export async function getAuditLog(params = {}) {
  const query = new URLSearchParams(params).toString();
  const path = `/acc/admin/audit-log/${query ? "?" + query : ""}`;
  return await request("GET", path, undefined, undefined, false);
}

export async function getAvailableRegions() {
  return await request("GET", "/acc/admin/regions/", undefined, undefined, false);
}

// ─── NEW: customers with their regions (for Register form) ───────────────────
// Returns: [{ id, name, regions: [{ id, name }] }]
// xroadz_admin  → all customers
// customer_admin → only their own customer
export async function getCustomersWithRegions() {
  return await request("GET", "/acc/admin/customers/", undefined, undefined, false);
}

export async function getUserRegions() {
  return request("GET", "/acc/user_regions/", undefined, {}, false);
}

export async function updateProfile(data) {
  return request("PATCH", "/acc/update_profile/", data, {}, false);
}
export async function getPasswordResetKeyInfo(key) {
  return await request("GET", URLs.RESET_PASSWORD, undefined, {
    "X-Password-Reset-Key": key,
  });
}

export async function changePassword(data) {
  return await request("POST", "/account/password/change", data);
}

export async function mfaAuthenticate(code, isRecovery = false) {
  if (isRecovery) {
    return await request("POST", "/auth/2fa/authenticate", { code });
  }
  return await request("POST", "/auth/2fa/authenticate", { code });
}

export async function getTOTPAuthenticator() {
  return await request("GET", "/acc/totp/setup/", undefined, undefined, false);
}

export async function activateTOTP(code) {
  return await request("POST", "/acc/totp/activate/", { code }, undefined, false);
}

export async function deactivateTOTP() {
  return await request("DELETE", "/account/authenticators/totp");
}

export async function getRecoveryCodes() {
  return await request("GET", "/acc/recovery-codes/", undefined, undefined, false);
}

export async function regenerateRecoveryCodes() {
  return await request("POST", "/acc/recovery-codes/regenerate/", undefined, undefined, false);
}
// ─── TENANT (per-customer schema) ──────────────────────────────────────────────

export async function getTenantContext() {
  return await request("GET", "/acc/tenant/context", undefined, undefined, false);
}

export async function switchTenant(customerId) {
  return await request("POST", "/acc/tenant/switch", { customer_id: customerId }, undefined, false);
}
